import { Dialog as BaseDialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type * as React from 'react';
import { Heading } from '../../components/ui/heading.tsx';
import Text from '../../components/ui/text.tsx';
import { cn } from '../../lib/utils.ts';

export type DialogProps = {
    content?: React.ReactNode;
    overlayClosable?: boolean;
    open?: boolean;
    showClose?: boolean;
    onClose?: () => void;
    onOpenChange?: (open: boolean) => void;
    onFocus?: React.FocusEventHandler<HTMLElement>;
    /**
     * `default` fills the screen on mobile (correct for forms / takeovers).
     * `compact` floats a small card with a gap on every edge (bottom-anchored
     * on mobile, centered on desktop) — for confirmations, not takeovers.
     */
    variant?: 'default' | 'compact';
} & React.PropsWithChildren;

export const Dialog = ({
    content,
    overlayClosable,
    open,
    showClose,
    onClose,
    children,
    onOpenChange,
    onFocus,
    variant = 'default',
}: DialogProps) => {
    const compact = variant === 'compact';
    return (
        <BaseDialog
            open={open ?? false}
            className="fixed inset-0 z-50 overflow-y-auto"
            onClose={(value) => {
                onOpenChange?.(value);
                if (overlayClosable) {
                    onClose?.();
                }
            }}
            onFocus={onFocus}
            transition
        >
            <DialogBackdrop
                transition
                className="fixed inset-0 z-0 bg-neutral-900/40 transition duration-300 ease-out data-closed:opacity-0"
            />
            <div
                className={cn(
                    'flex justify-center items-end min-h-dvh sm:p-8',
                    compact ? 'p-3 sm:items-center' : 'sm:items-start',
                )}
            >
                <DialogPanel
                    transition
                    className={cn(
                        'relative z-50 text-left transition duration-200 ease-out data-closed:scale-90 data-closed:opacity-0',
                        compact ? 'w-full max-w-sm' : 'inline-block w-auto align-middle max-sm:w-screen',
                    )}
                >
                    <div
                        className={cn(
                            // Flex column + max-h (no fixed height) lets the panel grow to its
                            // content, cap at the viewport, and scroll its body internally.
                            'flex flex-col w-full bg-white overflow-hidden max-h-[calc(100dvh-2rem)]',
                            compact
                                ? 'rounded-2xl shadow-2xl sm:shadow-xl max-sm:border max-sm:border-neutral-200'
                                : 'rounded-t-2xl shadow-xl sm:rounded-2xl',
                        )}
                    >
                        {showClose ? (
                            <button
                                className={cn(
                                    'absolute right-3 top-3 z-10 flex items-center',
                                    'justify-center rounded-lg h-8 w-8 text-neutral-400 transition-colors',
                                    'hover:bg-neutral-500/10 hover:text-neutral-500 active:bg-neutral-500/20',
                                )}
                                onClick={() => {
                                    onClose?.();
                                }}
                            >
                                <XMarkIcon className="h-5 w-5 text-neutral-700" />
                            </button>
                        ) : null}
                        {content}
                        {children}
                    </div>
                </DialogPanel>
            </div>
        </BaseDialog>
    );
};

type DialogContentProps = {
    className?: string;
} & React.PropsWithChildren;

const DialogContent = ({ children, className }: DialogContentProps) => {
    return <div className={cn('flex w-full max-w-sm flex-col gap-3 p-4', className)}>{children}</div>;
};

type DialogHeaderProps = {
    className?: string;
} & React.PropsWithChildren;

const DialogHeader = ({ children, className }: DialogHeaderProps) => {
    return <div className={cn('flex flex-row gap-2 bg-white px-4 pt-3', className)}>{children}</div>;
};

const DialogIcon = ({ children }: React.PropsWithChildren) => {
    return <div className="[&>svg]:mt-0.5 [&>svg]:h-4 [&>svg]:w-4">{children}</div>;
};

const DialogHeaderText = ({ children }: React.PropsWithChildren) => {
    return <div className="flex flex-col gap-2">{children}</div>;
};

const DialogTitle = ({ children }: React.PropsWithChildren) => {
    return (
        <Heading variant="h1" className="text-sm font-semibold text-neutral-900">
            {children}
        </Heading>
    );
};

const DialogDescription = ({ children }: React.PropsWithChildren) => {
    return (
        <Text color={'neutral-600'} className="text-muted-foreground">
            {children}
        </Text>
    );
};

type DialogActionsProps = {
    className?: string;
} & React.PropsWithChildren;

const DialogActions = ({ children, className }: DialogActionsProps) => {
    return (
        <div className={cn('align-center flex w-full items-center justify-end gap-3', className)}>
            {children}
        </div>
    );
};

Dialog.Content = DialogContent;
Dialog.Header = DialogHeader;
Dialog.Icon = DialogIcon;
Dialog.HeaderText = DialogHeaderText;
Dialog.Title = DialogTitle;
Dialog.Description = DialogDescription;
Dialog.Actions = DialogActions;

export default Dialog;
